import Icon from '@atoms/Icon/Icon';
import StickyNavPosition from '@models/enums/StickyNavPositionEnum';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useStickyBuyBarStore from '@state/StickyBuyBarStore';
import useStickyNavPositionStore from '@state/StickyNavStore';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useWindowScrollPositions from '@utils/hooks/useWindowScrollPosition';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  readonly footerInView: boolean;
  readonly backToTopButtonText?: string;
  readonly enableStickyBackToTopButton: boolean;
}

const Button = ({
  backToTopButtonText,
}: Pick<Props, 'backToTopButtonText'>) => (
  <motion.button
    initial={{ scale: 1, rotate: 0 }}
    whileHover={{ scale: 1.2, rotate: 360 }}
    transition={{ duration: 1 }}
    onClick={() =>
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // We disable transitions for pages where we have background animation enabled
        // in order to avoid small flickers caused by fast scrolling with back to top button
        const animatedBackgroundPageContainer = document.getElementById(
          'animated-background-page-container'
        );
        if (animatedBackgroundPageContainer) {
          animatedBackgroundPageContainer.style.transition = '';
        }
      })
    }
    aria-label={backToTopButtonText}
    className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow text-black-100 active:bg-yellow-500 md:flex xl:active:bg-yellow-500 xl:hover:bg-yellow-100"
  >
    <Icon name="upArrow" size="1.25rem" />
  </motion.button>
);

const BackToTopButton = ({
  footerInView = false,
  backToTopButtonText,
  enableStickyBackToTopButton,
}: Props) => {
  const [backToTopIsSticky, setBackToTopIsSticky] = useState(false);
  const { scrollY } = useWindowScrollPositions();
  const { stickyNavPosition } = useStickyNavPositionStore((state) => ({
    stickyNavPosition: state.stickyNavPosition,
  }));
  const { stickyBuyBarIsInView } = useStickyBuyBarStore((state) => ({
    stickyBuyBarIsInView: state.stickyBuyBarIsInView,
  }));
  const { stickyBuyBarHeight, tabbedProductNavigationBottomHeight } =
    useComponentsHeightsStore((state) => ({
      stickyBuyBarHeight: state.stickyBuyBarHeight,
      tabbedProductNavigationBottomHeight:
        state.tabbedProductNavigationBottomHeight,
    }));
  const isBelowLargeDesktop = useMediaQuery(1440);
  const isMobile = useMediaQuery(768);
  const scrollDistanceForButtonVisibility = isBelowLargeDesktop ? 1000 : 2000;
  const isStickyButtonVisible = scrollY > scrollDistanceForButtonVisibility;

  useEffect(() => {
    const stickyBackToTopCondition =
      enableStickyBackToTopButton &&
      (stickyNavPosition === StickyNavPosition.Top ||
        (!isMobile && stickyNavPosition === StickyNavPosition.Bottom) ||
        stickyBuyBarIsInView);

    setBackToTopIsSticky(stickyBackToTopCondition);
  }, [
    enableStickyBackToTopButton,
    stickyNavPosition,
    isMobile,
    stickyBuyBarIsInView,
  ]);

  return (
    <div
      id={`backToTopButton${enableStickyBackToTopButton ? 'WithFixed' : ''}`}
      className="w-full"
    >
      <AnimatePresence>
        {footerInView ? (
          <motion.div
            key="relativePosition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="container relative flex items-center justify-end xl:justify-start xl:pr-0"
          >
            {backToTopButtonText && (
              <p className="mr-5 text-grey-500 text-xs">
                {backToTopButtonText}
              </p>
            )}

            <Button backToTopButtonText={backToTopButtonText} />
          </motion.div>
        ) : (
          backToTopIsSticky && (
            <motion.div
              key="fixedPosition"
              initial={{ opacity: 0 }}
              animate={{
                opacity: isStickyButtonVisible ? 1 : 0,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={classNames(
                'fixed right-2.5 bottom-2.5 z-max flex items-center xl:right-7.5 xl:bottom-7.5',
                {
                  'pointer-events-none': !isStickyButtonVisible,
                }
              )}
              style={{
                ...(stickyBuyBarIsInView
                  ? { bottom: stickyBuyBarHeight + 20 }
                  : {}),
                ...(tabbedProductNavigationBottomHeight > 0
                  ? { bottom: tabbedProductNavigationBottomHeight + 20 }
                  : {}),
              }}
            >
              <Button backToTopButtonText={backToTopButtonText} />
            </motion.div>
          )
        )}
      </AnimatePresence>
    </div>
  );
};

export default BackToTopButton;
